import { Injectable } from '@angular/core';

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { environment } from 'src/environments/environment';
import { fromServerToSubscription } from '../helpers/store-integrations.helper';
import {
  StorePaymentAPI,
  StorePurchaseApi,
  StoreSubscriptionAPI,
} from '../store-api';
import { ApiService } from './api.service';

@Injectable()
export class ChargebeeStoreApiService
  extends ApiService<any>
  implements StorePaymentAPI, StorePurchaseApi, StoreSubscriptionAPI
{
  protected APIUrl =
    environment.SPRING_STORE_ENDPOINT_URL + this.getResourceUrl();

  constructor(
    protected httpClient: HttpClient,
    protected companyStateService: CompanyStateService,
  ) {
    super(httpClient);
  }

  getResourceUrl(): string {
    return '/v2';
  }

  estimatePurchase(jsonData) {
    const params = new HttpParams().set('jsonData', jsonData);

    return firstValueFrom(
      super.requestAsObservable('', 'GET', 'purchase/estimate', params),
    );
  }

  preparePurchase(jsonData) {
    const params = new HttpParams().set('jsonData', jsonData);

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'purchase/prepare', params),
    );
  }

  purchase(jsonData) {
    const params = new HttpParams().set('jsonData', jsonData);

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'purchase/put', params),
    );
  }

  preparePayment(paymentMethodId, invoiceId, companyId, token) {
    const headers = new HttpHeaders().set('token', token);
    const params = new HttpParams()
      .set('paymentMethodId', paymentMethodId)
      .set('companyId', companyId);

    return firstValueFrom(
      super.requestWithHeadersAsObservable(
        '',
        'POST',
        `payment/prepare/${invoiceId}`,
        headers,
        params,
      ),
    );
  }

  collectPayment(paymentIntentId, invoiceId, companyId, token) {
    const headers = new HttpHeaders().set('token', token);
    const params = new HttpParams()
      .set('paymentIntentId', paymentIntentId)
      .set('companyId', companyId);

    return firstValueFrom(
      super.requestWithHeadersAsObservable(
        '',
        'POST',
        `payment/collect/${invoiceId}`,
        headers,
        params,
      ),
    );
  }

  getSubscriptions(search, cursor?) {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('cursor', cursor || '')
      .set('count', search.count ? search.count.toString() : '');

    return firstValueFrom(
      this.requestAsObservable(
        '',
        'GET',
        'integrations/subscriptions/list',
        params,
      ).pipe(
        map((response) => {
          if (response == null) {
            return { items: [] };
          }

          if (response.items == null) {
            return { ...response, items: [] };
          }

          if (!Array.isArray(response.items)) {
            throw new Error(
              'The subscriptions could not be loaded. Invalid response from the subscriptions service.',
            );
          }

          return {
            ...response,
            items: response.items.map(fromServerToSubscription),
          };
        }),
      ),
    );
  }

  getSubscription(
    subscriptionId: string,
    withScheduledChanges?: boolean,
  ): Promise<any> {
    const params = new HttpParams()
      .set('subscriptionId', subscriptionId)
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('withScheduledChanges', !!withScheduledChanges);

    return firstValueFrom(
      this.requestAsObservable(
        '',
        'GET',
        'integrations/subscription',
        params,
      ).pipe(
        map((response) => {
          response.item = fromServerToSubscription(response.item);
          return response;
        }),
      ),
    );
  }
}
